import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Activity, AlertCircle, ChevronRight } from 'lucide-react';
import { Job } from '../lib/types';
import { useAuth } from '../contexts/AuthContext';
import { formatDateTime, titleCaseSnake } from '../lib/text';
import { JobStatusIcon, jobStatusBgColor } from '../lib/jobs';
import { PageLoader } from '../components/PageLoader';

const PAGE = 50;

// The newest page, polled, laid over the jobs already loaded: what it holds replaces them, and
// the older pages scrolled into stay below it.
function withHead(loaded: Job[] | null, head: Job[]): Job[] {
  if (!loaded || head.length === 0) return head;
  const at = loaded.findIndex(j => j.id === head[head.length - 1].id);
  if (at !== -1) return [...head, ...loaded.slice(at + 1)];
  if (head.length < PAGE) return head;
  const ids = new Set(head.map(j => j.id));
  return [...head, ...loaded.filter(j => !ids.has(j.id))];
}

export default function JobsPage() {
  const { authFetch } = useAuth();
  const [jobs, setJobs] = useState<Job[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinel = useRef<HTMLDivElement | null>(null);
  const loadedOnce = useRef(false);

  useEffect(() => {
    // Chain the next poll off the previous one's completion so one request is in flight at a time.
    let cancelled = false;
    let timer: number | null = null;
    const tick = async () => {
      try {
        const response = await authFetch('/jobs');
        if (cancelled) return;
        if (!response.ok) throw new Error('Failed to load jobs');
        const head: Job[] = await response.json();
        if (!loadedOnce.current) setHasMore(head.length === PAGE);
        loadedOnce.current = true;
        setJobs(prev => withHead(prev, head));
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load jobs');
        setJobs([]);
      } finally {
        if (!cancelled) timer = window.setTimeout(tick, 3000);
      }
    };
    tick();
    return () => {
      cancelled = true;
      if (timer !== null) clearTimeout(timer);
    };
  }, []);

  const loadMore = useCallback(async () => {
    const last = jobs?.[jobs.length - 1];
    if (!last || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await authFetch(`/jobs?before=${encodeURIComponent(`${last.created_at}|${last.id}`)}`);
      if (!res.ok) throw new Error('Failed to load older jobs');
      const page: Job[] = await res.json();
      setJobs(prev => {
        const ids = new Set((prev ?? []).map(j => j.id));
        return [...(prev ?? []), ...page.filter(j => !ids.has(j.id))];
      });
      setHasMore(page.length === PAGE);
    } catch {
      // Stop here: a sentinel still in view would otherwise retry at once, again and again.
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [authFetch, jobs, loadingMore]);

  useEffect(() => {
    const el = sentinel.current;
    if (!el || !hasMore) return;
    const observer = new IntersectionObserver(entries => { if (entries[0].isIntersecting) loadMore(); });
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, loadMore]);

  if (jobs === null) return <PageLoader label="Loading jobs..." />;

  return (
    <div>
      <div className="pb-6 mb-6 border-b border-softgrey">
        <h3 className="text-lg font-semibold">Jobs</h3>
        <p className="text-sm text-coolslate mt-1">Background ingestion and generation work.</p>
      </div>

      {error && (
        <div className="mb-6 bg-state-error/10 border border-state-error/30 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-state-error shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="font-semibold text-graphite mb-1">Failed to load jobs</h4>
            <p className="text-sm text-state-error">{error}</p>
          </div>
          <button
            onClick={() => setError(null)}
            className="text-state-error hover:text-state-error font-bold text-xl leading-none"
          >
            ×
          </button>
        </div>
      )}

      {jobs.length === 0 ? (
        <div className="py-12 text-center">
          <Activity className="w-16 h-16 text-coolslate/70 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-graphite mb-2">No jobs yet</h3>
          <p className="text-coolslate">Ingestion jobs will appear here</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {jobs.map((job) => (
            <Link
              key={job.id}
              to={`/jobs/${job.id}`}
              className="bg-white rounded-lg shadow-sm border border-softgrey p-6 cursor-pointer hover:bg-offwhite transition-colors block"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4 flex-1 min-w-0">
                  <div className={`w-12 h-12 rounded-xl border border-softgrey flex items-center justify-center shrink-0 ${jobStatusBgColor(job.status)}`}>
                    <JobStatusIcon status={job.status} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <h4 className="font-semibold text-graphite">{job.source || 'Unknown source'}</h4>
                      {job.task_type && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-brand/10 text-brand font-medium">
                          {titleCaseSnake(job.task_type)}
                        </span>
                      )}
                    </div>
                    {job.error && job.status === 'failed' && (
                      <div className="mt-3 p-2 bg-state-error/10 border border-state-error/30 rounded text-sm text-state-error">
                        <strong>Error:</strong> {job.error}
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-3">
                      {job.created_at && (
                        <>
                          <Activity className="w-4 h-4 text-coolslate/70" />
                          <span className="text-sm text-coolslate">{formatDateTime(job.created_at)}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-coolslate/70 shrink-0" />
              </div>
            </Link>
          ))}
          {hasMore && (
            <div ref={sentinel} className="py-4 text-center text-sm text-coolslate">
              {loadingMore ? 'Loading older jobs...' : ''}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
